import { api } from './client';

/**
 * LOS CLIENTES DE CONVERSIA (solo el OWNER). Prefijo: /api/operacion/clientes.
 * Tipos espejo de apps/api/src/services/negocios-cliente.service.ts.
 *
 * No confundir con `clientes.api.ts`: esa es la cartera de clientes de UN
 * negocio (CRM). Esto es la lista de negocios a los que Conversia sirve.
 */

export type TipoCliente = 'nuevo' | 'reflejo' | 'vip' | 'pendiente';

export interface ClienteResumen {
  workspaceId: string;
  nombre: string;
  slug: string;
  tipo: TipoCliente;
  programaUrl: string | null;
  embebible: boolean;
  activo: boolean;
  usuarios: number;
  modulos: string[];
}

export interface AltaClienteExterno {
  nombre: string;
  slug: string;
  tipo: Exclude<TipoCliente, 'nuevo'>;
  programaUrl?: string;
  embebible?: boolean;
}

export interface AccesoDado {
  userId: string;
  /** `false` = la cuenta ya existía: entra con su propia clave. */
  usuarioNuevo: boolean;
}

export const listarClientes = () => api.get<ClienteResumen[]>('/api/operacion/clientes');

/** El estado de cada Conversia (apps/api/src/services/estado-conversias.service.ts). */
export interface EstadoConversia {
  workspaceId: string;
  vivo: boolean;
  ms: number | null;
  canales: { total: number; conectados: number; caidos: { nombre: string; negocio: string | null; minutosCaido: number | null }[] } | null;
  resumen: string;
  pase: boolean;
  revisadoEn: string;
}
export const estadoDeTodos = (fresco = false) =>
  api.get<{ estados: EstadoConversia[]; puedeEntrar: boolean }>(`/api/operacion/estado${fresco ? '?fresco=1' : ''}`);

/** El pase para entrar a ese Conversia como su dueño, sin clave (apps/api/src/services/pase-global.service.ts). */
export const pedirPase = (workspaceId: string) =>
  api.post<{ url: string; expira: string }>(`/api/operacion/clientes/${encodeURIComponent(workspaceId)}/pase`);

export const crearClienteExterno = (input: AltaClienteExterno) =>
  api.post<{ workspaceId: string }>('/api/operacion/clientes', input);

export const darAcceso = (
  workspaceId: string,
  input: { email: string; nombre?: string; password?: string; tambienEn?: boolean },
) => api.post<AccesoDado>(`/api/operacion/clientes/${encodeURIComponent(workspaceId)}/acceso`, input);

/** Lo que ve quien entra a «Mi Conversia» en su negocio activo (cualquier sesión). */
export interface MiConversiaDatos {
  nombre: string;
  tipo: TipoCliente;
  programaUrl: string | null;
  embebible: boolean;
}

export const miConversia = () => api.get<MiConversiaDatos>('/api/mi-conversia');

// ── Solicitudes de alta (solo el OWNER). Prefijo: /api/operacion/solicitudes.
// Tipos espejo de apps/api/src/services/alta-cliente.service.ts.

export type EstadoSolicitud = 'BORRADOR' | 'ENVIADA' | 'APROBADA' | 'RECHAZADA';

export interface SolicitudResumen {
  id: string;
  negocioNombre: string;
  email: string;
  nombre: string | null;
  estado: EstadoSolicitud;
  modulosPropuestos: string[];
  audios: number;
  workspaceId: string | null;
  updatedAt: string;
}

export interface HallazgoDeAnalisis {
  problema: string;
  solucion: string;
  modulos: string[];
  /** Copiada tal cual de lo que contó el cliente. */
  frase: string;
}

export interface SolicitudDetalle {
  id: string;
  negocioNombre: string;
  textoCliente: string;
  estado: EstadoSolicitud;
  motivoRechazo: string | null;
  createdAt: string;
  updatedAt: string;
  email: string;
  nombre: string | null;
  workspaceId: string | null;
  modulosPropuestos: string[];
  analisis: { resumen: string; hallazgos: HallazgoDeAnalisis[]; modulos: string[] } | null;
  audios: Array<{ mime: string; bytes: number; transcripcion: string | null; creadoEn: string; url: string }>;
}

/** Un módulo del catálogo (GET /api/modulos). */
export interface ModuloDelCatalogo {
  id: string;
  nombre: string;
  descripcion: string;
  rolMinimo: string;
  construido: boolean;
}

export const listarSolicitudes = () => api.get<SolicitudResumen[]>('/api/operacion/solicitudes');

export const verSolicitud = (id: string) =>
  api.get<SolicitudDetalle>(`/api/operacion/solicitudes/${encodeURIComponent(id)}`);

/** El audio va con la sesión: un <audio src> no puede mandarla, así que llega como Blob. */
export const audioDeSolicitud = (url: string) => api.get<Blob>(url, { blob: true });

export const aprobarSolicitud = (id: string, input: { nombre: string; slug: string; modulos: string[] }) =>
  api.post<{ workspaceId: string; slug: string; nombre: string }>(
    `/api/operacion/solicitudes/${encodeURIComponent(id)}/aprobar`,
    input,
  );

export const rechazarSolicitud = (id: string, motivo: string) =>
  api.post<SolicitudDetalle>(`/api/operacion/solicitudes/${encodeURIComponent(id)}/rechazar`, { motivo });

export const catalogoDeModulos = () =>
  api.get<{ modulos: ModuloDelCatalogo[] }>('/api/modulos').then((r) => r.modulos);
